import { Button } from '@/components/ui/button';

export interface TablePaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  /** Noun for the count line, e.g. "invoices", "entries". */
  itemLabel?: string;
  className?: string;
}

/**
 * The app-wide table pagination bar: a "Showing X to Y of Z" count on the
 * left, page controls on the right. Numbered pages around the current one,
 * with ±5/±10 jumps appearing only when the page count warrants them.
 * Renders nothing for a single page.
 */
export function TablePagination({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  itemLabel = 'results',
  className,
}: TablePaginationProps) {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  if (totalPages <= 1) return null;

  const go = (page: number) => onPageChange(Math.min(Math.max(page, 1), totalPages));

  // Window of up to 5 page numbers centred on the current page.
  const windowSize = Math.min(5, totalPages);
  const pages = Array.from({ length: windowSize }, (_, i) => {
    if (totalPages <= 5) return i + 1;
    if (currentPage <= 3) return i + 1;
    if (currentPage >= totalPages - 2) return totalPages - 4 + i;
    return currentPage - 2 + i;
  });

  const first = (currentPage - 1) * itemsPerPage + 1;
  const last = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 ${className ?? ''}`}>
      <div className="text-sm text-muted-foreground">
        Showing {first} to {last} of {totalItems} {itemLabel}
      </div>
      <div className="flex items-center gap-1">
        {totalPages > 10 && (
          <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => go(currentPage - 10)}>
            -10
          </Button>
        )}
        <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => go(currentPage - 1)}>
          Previous
        </Button>
        {pages.map((page) => (
          <Button
            key={page}
            variant={page === currentPage ? 'default' : 'ghost'}
            size="sm"
            className="w-9"
            aria-current={page === currentPage ? 'page' : undefined}
            onClick={() => go(page)}
          >
            {page}
          </Button>
        ))}
        <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => go(currentPage + 1)}>
          Next
        </Button>
        {totalPages > 10 && (
          <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => go(currentPage + 10)}>
            +10
          </Button>
        )}
      </div>
    </div>
  );
}
